/**
 * CoinGecko API service for fetching cryptocurrency prices.
 * Uses the free /simple/price endpoint with batched requests.
 */

// Top ~100 coins: symbol → CoinGecko ID
const COIN_MAP: Record<string, string> = {
  BTC: 'bitcoin',
  ETH: 'ethereum',
  USDT: 'tether',
  BNB: 'binancecoin',
  SOL: 'solana',
  USDC: 'usd-coin',
  XRP: 'ripple',
  DOGE: 'dogecoin',
  TRX: 'tron',
  ADA: 'cardano',
  AVAX: 'avalanche-2',
  SHIB: 'shiba-inu',
  DOT: 'polkadot',
  LINK: 'chainlink',
  BCH: 'bitcoin-cash',
  TON: 'the-open-network',
  DAI: 'dai',
  LEO: 'leo-token',
  LTC: 'litecoin',
  NEAR: 'near',
  MATIC: 'matic-network',
  POL: 'matic-network',
  UNI: 'uniswap',
  ICP: 'internet-computer',
  PEPE: 'pepe',
  KAS: 'kaspa',
  ETC: 'ethereum-classic',
  APT: 'aptos',
  XMR: 'monero',
  RENDER: 'render-token',
  FET: 'fetch-ai',
  XLM: 'stellar',
  STX: 'blockstack',
  CRO: 'crypto-com-chain',
  ATOM: 'cosmos',
  FIL: 'filecoin',
  OKB: 'okb',
  ARB: 'arbitrum',
  IMX: 'immutable-x',
  MKR: 'maker',
  HBAR: 'hedera-hashgraph',
  SUI: 'sui',
  OP: 'optimism',
  VET: 'vechain',
  INJ: 'injective-protocol',
  GRT: 'the-graph',
  RUNE: 'thorchain',
  THETA: 'theta-token',
  FTM: 'fantom',
  ALGO: 'algorand',
  SEI: 'sei-network',
  LDO: 'lido-dao',
  AAVE: 'aave',
  SAND: 'the-sandbox',
  MANA: 'decentraland',
  AXS: 'axie-infinity',
  FLOW: 'flow',
  XTZ: 'tezos',
  EGLD: 'elrond-erd-2',
  ENS: 'ethereum-name-service',
  SNX: 'havven',
  GALA: 'gala',
  QNT: 'quant-network',
  CHZ: 'chiliz',
  COMP: 'compound-governance-token',
  CRV: 'curve-dao-token',
  RPL: 'rocket-pool',
  DYDX: 'dydx',
  APE: 'apecoin',
  WLD: 'worldcoin-wld',
  TIA: 'celestia',
  JUP: 'jupiter-exchange-solana',
  PYTH: 'pyth-network',
  W: 'wormhole',
  BONK: 'bonk',
  WIF: 'dogwifcoin',
  FLOKI: 'floki',
  ONDO: 'ondo-finance',
  PENDLE: 'pendle',
  ENA: 'ethena',
  EIGEN: 'eigenlayer',
  BOME: 'book-of-meme',
  TAO: 'bittensor',
  FTN: 'fasttoken',
  STRK: 'starknet',
  ZK: 'zksync',
  BLUR: 'blur',
  JTO: 'jito-governance-token',
  MEME: 'memecoin-2',
  WOO: 'woo-network',
  ZRO: 'layerzero',
};

// CoinGecko ID → Coinbase base symbol. Later entries win, so POL beats MATIC.
const ID_TO_SYMBOL: Record<string, string> = Object.entries(COIN_MAP).reduce(
  (acc, [symbol, id]) => ({ ...acc, [id]: symbol }),
  {} as Record<string, string>
);

const COINBASE_API = 'https://api.exchange.coinbase.com/products';
const DAY_MS = 24 * 60 * 60 * 1000;

export interface CryptoPriceData {
  usd: number;
  usd_24h_change: number;
}

const PRICE_CACHE_KEY = 'tallyup_crypto_last_prices';

const readPriceCache = (): Record<string, CryptoPriceData> => {
  try {
    return JSON.parse(localStorage.getItem(PRICE_CACHE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writePriceCache = (prices: Record<string, CryptoPriceData>): void => {
  try {
    localStorage.setItem(PRICE_CACHE_KEY, JSON.stringify({ ...readPriceCache(), ...prices }));
  } catch {
    /* storage full or blocked; the live fetch still works */
  }
};

/**
 * Fill holdings with the last prices seen on this device so net worth doesn't
 * dip to $0 crypto while live prices load. Display only — snapshots use live prices.
 */
export const applyCachedCryptoPrices = <T extends { coinGeckoId: string; symbol: string; amount: number; currentPrice: number; totalValue: number; change24h: number }>(
  holdings: T[]
): T[] => {
  const cache = readPriceCache();
  return holdings.map(h => {
    const cached = cache[h.coinGeckoId || resolveSymbolToId(h.symbol) || ''];
    if (!cached || h.currentPrice > 0) return h;
    return { ...h, currentPrice: cached.usd, totalValue: h.amount * cached.usd, change24h: cached.usd_24h_change };
  });
};

/** Holding ticker wins over the reverse map, so unmapped CoinGecko IDs still fall back. */
const coinbaseSymbolFor = (id: string, symbolById?: Record<string, string>): string | undefined =>
  (symbolById?.[id] || ID_TO_SYMBOL[id] || '').trim().toUpperCase() || undefined;

const fetchCoinbasePrice = async (symbol: string | undefined): Promise<CryptoPriceData | null> => {
  if (!symbol) return null;
  try {
    const response = await fetch(`${COINBASE_API}/${symbol}-USD/stats`);
    if (!response.ok) return null;
    const data = await response.json();
    const last = parseFloat(data.last);
    const open = parseFloat(data.open);
    if (!Number.isFinite(last) || last <= 0) return null;
    return {
      usd: last,
      usd_24h_change: Number.isFinite(open) && open > 0 ? ((last - open) / open) * 100 : 0,
    };
  } catch {
    return null;
  }
};

/** Daily closes as [timestampMs, close][], oldest → newest. Coinbase caps each call at 300 candles. */
const fetchCoinbaseDailyCloses = async (
  symbol: string,
  start?: Date,
  end?: Date
): Promise<Array<[number, number]>> => {
  const range = start && end ? `&start=${start.toISOString()}&end=${end.toISOString()}` : '';
  const response = await fetch(`${COINBASE_API}/${symbol}-USD/candles?granularity=86400${range}`);
  if (!response.ok) return [];
  const candles: number[][] = await response.json();
  // Candle shape: [time, low, high, open, close, volume], newest first
  return candles.map(c => [c[0] * 1000, c[4]] as [number, number]);
};

const fetchCoinbasePeriodSeries = async (symbol: string | undefined): Promise<Array<[number, number]>> => {
  if (!symbol) return [];
  try {
    const now = Date.now();
    const [recent, yearAgo] = await Promise.all([
      fetchCoinbaseDailyCloses(symbol),
      fetchCoinbaseDailyCloses(symbol, new Date(now - 372 * DAY_MS), new Date(now - 358 * DAY_MS)),
    ]);
    return [...recent, ...yearAgo].sort((a, b) => a[0] - b[0]);
  } catch {
    return [];
  }
};

export interface CryptoPeriodReturns {
  daily: number | null;
  weekly: number | null;
  monthly: number | null;
  annual: number | null;
}

const periodCache = new Map<string, { at: number; value: CryptoPeriodReturns }>();
const PERIOD_CACHE_TTL_MS = 15 * 60 * 1000;

const pctChange = (from: number | null | undefined, to: number | null | undefined): number | null => {
  if (from == null || to == null || !Number.isFinite(from) || !Number.isFinite(to) || from === 0) {
    return null;
  }
  return ((to - from) / from) * 100;
};

/** prices: [timestampMs, price][] oldest → newest */
const returnsFromPriceSeries = (prices: Array<[number, number]>): CryptoPeriodReturns => {
  if (!prices || prices.length < 2) {
    return { daily: null, weekly: null, monthly: null, annual: null };
  }
  const last = prices[prices.length - 1][1];
  const lastTs = prices[prices.length - 1][0];
  const nearDaysAgo = (days: number): number | null => {
    const target = lastTs - days * 24 * 60 * 60 * 1000;
    let bestIdx = -1;
    let bestDelta = Infinity;
    for (let i = 0; i < prices.length; i++) {
      const delta = Math.abs(prices[i][0] - target);
      if (delta < bestDelta) {
        bestDelta = delta;
        bestIdx = i;
      }
    }
    return bestIdx >= 0 ? prices[bestIdx][1] : null;
  };

  // Prefer ~24h lookback for daily when series is dense enough
  const dayAgo = nearDaysAgo(1);
  const daily = dayAgo != null ? pctChange(dayAgo, last) : pctChange(prices[prices.length - 2][1], last);

  return {
    daily,
    weekly: pctChange(nearDaysAgo(7), last),
    monthly: pctChange(nearDaysAgo(30), last),
    annual: pctChange(nearDaysAgo(365), last),
  };
};

/**
 * Fetch 1D/1W/1M/1Y price returns for CoinGecko IDs via market_chart.
 * Uses a small concurrency limit to stay friendly to the free API.
 */
export const fetchCryptoPeriodReturns = async (
  ids: string[],
  symbolById?: Record<string, string>
): Promise<Record<string, CryptoPeriodReturns>> => {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (unique.length === 0) return {};

  const now = Date.now();
  const out: Record<string, CryptoPeriodReturns> = {};
  const missing: string[] = [];

  for (const id of unique) {
    const hit = periodCache.get(id);
    if (hit && now - hit.at < PERIOD_CACHE_TTL_MS) {
      out[id] = hit.value;
    } else {
      missing.push(id);
    }
  }

  const concurrency = 2;
  for (let i = 0; i < missing.length; i += concurrency) {
    const chunk = missing.slice(i, i + concurrency);
    await Promise.all(
      chunk.map(async id => {
        try {
          const url =
            `https://api.coingecko.com/api/v3/coins/${encodeURIComponent(id)}/market_chart` +
            `?vs_currency=usd&days=365`;
          const response = await fetch(url);
          let prices: Array<[number, number]> = [];
          if (response.ok) {
            const data = await response.json();
            prices = data.prices || [];
          }
          if (prices.length < 2) prices = await fetchCoinbasePeriodSeries(coinbaseSymbolFor(id, symbolById));
          const value = returnsFromPriceSeries(prices);
          if (prices.length >= 2) periodCache.set(id, { at: now, value });
          out[id] = value;
        } catch (error) {
          console.warn(`CoinGecko market_chart failed for ${id}:`, error);
          out[id] = returnsFromPriceSeries(
            await fetchCoinbasePeriodSeries(coinbaseSymbolFor(id, symbolById))
          );
        }
      })
    );
  }

  return out;
};

/**
 * Resolve a coin symbol (e.g., BTC) to its CoinGecko ID (e.g., bitcoin).
 * Returns the provided fallbackId if no match is found.
 */
export const resolveSymbolToId = (symbol: string, fallbackId?: string): string | undefined => {
  return COIN_MAP[symbol.toUpperCase()] || fallbackId || undefined;
};

/**
 * Fetch prices for a list of CoinGecko IDs.
 * Returns a map of id → { usd, usd_24h_change }.
 * Gracefully returns empty on failure.
 */
export const fetchCryptoPrices = async (
  ids: string[],
  symbolById?: Record<string, string>
): Promise<Record<string, CryptoPriceData>> => {
  if (ids.length === 0) return {};

  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
  if (uniqueIds.length === 0) return {};

  const result: Record<string, CryptoPriceData> = {};

  try {
    const url = `https://api.coingecko.com/api/v3/simple/price?ids=${uniqueIds.join(',')}&vs_currencies=usd&include_24hr_change=true`;
    const response = await fetch(url);

    if (response.ok) {
      const data = await response.json();
      for (const id of uniqueIds) {
        if (data[id]?.usd) {
          result[id] = {
            usd: data[id].usd,
            usd_24h_change: data[id].usd_24h_change ?? 0,
          };
        }
      }
    } else {
      console.warn(`CoinGecko API returned ${response.status}, falling back to Coinbase`);
    }
  } catch (error) {
    console.warn('CoinGecko API error, falling back to Coinbase:', error);
  }

  const missing = uniqueIds.filter(id => !result[id]);
  const fallback = await Promise.all(
    missing.map(id => fetchCoinbasePrice(coinbaseSymbolFor(id, symbolById)))
  );
  missing.forEach((id, i) => {
    const price = fallback[i];
    if (price) result[id] = price;
  });

  writePriceCache(result);
  return result;
};
